// 📄 components/organizacion/GestionarMiembrosModal.tsx
// Sub-fase M1+M2: gestión de miembros de una organización — ver miembros
// actuales + invitaciones pendientes, invitar, cambiar rol, remover.
// Standalone (recibe orgId/orgNombre/orgType/miRol por props) para poder
// abrirse tanto desde OrgSwitcher.tsx (topbar) como, más adelante, desde
// OrganizationsSection.tsx (admin panel) — no vive dentro de ninguno de
// los dos. Mismo estilo visual que CreateTeamOrgModal.tsx.
import React, { useEffect, useState } from 'react';
import { X, UserPlus, RefreshCw, Crown, Shield, Trash2, Mail, Clock, Send } from 'lucide-react';
import { reenviarInvitacion } from '../../services/activacionService.ts';
import { useAuth } from '../../context/AuthContext.tsx';
import { findPlatformUserByEmail } from '../../services/userService.ts';
import {
  getOrgMembers,
  getPendingInvitations,
  addExistingUserToOrg,
  createPendingInvitation,
  crearCuentaInvitadoOrg,
  avisarInvitacionPendiente,
  changeOrgMemberRole,
  removeOrgMember,
  revokeInvitation,
  OrgMemberConNombre
} from '../../services/organizationService.ts';
import { OrgType, OrgRoleAsignable, PendingInvitation } from '../../types/organization';
import LimiteUsoAlerta from '../ui/LimiteUsoAlerta.tsx';

interface GestionarMiembrosModalProps {
  orgId: string;
  orgNombre: string;
  orgType: OrgType;
  onClose: () => void;
}

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  miembro: 'Miembro',
  invitado: 'Invitado'
};

const fecha = (ts: any): string => {
  if (!ts?.toDate) return '—';
  return ts.toDate().toLocaleDateString('es', { year: 'numeric', month: 'short', day: 'numeric' });
};

export default function GestionarMiembrosModal({ orgId, orgNombre, orgType, onClose }: GestionarMiembrosModalProps) {
  const { user, profile, organizations } = useAuth();
  const miRol = organizations.find((o) => o.id === orgId)?.role;
  const puedoGestionar = miRol === 'owner' || miRol === 'admin';

  const [miembros, setMiembros] = useState<OrgMemberConNombre[]>([]);
  const [pendientes, setPendientes] = useState<(PendingInvitation & { id: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [rol, setRol] = useState<OrgRoleAsignable>('miembro');
  const [inviting, setInviting] = useState(false);
  // Paso 2 cuando el correo no tiene cuenta: se pide el nombre y se le crea
  // la cuenta con el correo "Crear mi contraseña" (igual que en proyectos).
  const [pedirNombre, setPedirNombre] = useState(false);
  const [nombreCompleto, setNombreCompleto] = useState('');

  // uid en el que hay una acción (cambiar rol / remover) en curso, para
  // deshabilitar solo esa fila en vez de la lista entera.
  const [accionEnCurso, setAccionEnCurso] = useState<string | null>(null);

  const cargar = async () => {
    setLoading(true);
    setError(null);
    try {
      const [m, p] = await Promise.all([getOrgMembers(orgId), getPendingInvitations(orgId)]);
      setMiembros(m);
      setPendientes(p);
    } catch (err: any) {
      setError(err.message || 'Error cargando miembros.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const handleInvitar = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) { setError('El email es obligatorio.'); return; }
    if (!user) { setError('Sesión no disponible, recargá la página.'); return; }

    setInviting(true);
    try {
      if (pedirNombre) {
        if (!nombreCompleto.trim()) { setError('Escribe el nombre de la persona.'); return; }
        await createPendingInvitation(orgId, orgNombre, orgType, normalizedEmail, rol, user.uid);
        try {
          const { correoEnviado } = await crearCuentaInvitadoOrg(orgId, normalizedEmail, nombreCompleto.trim());
          setSuccess(correoEnviado
            ? `Se creó la cuenta de ${normalizedEmail}. Le llegará un correo para crear su contraseña y, al entrar, quedará como ${ROLE_LABELS[rol]} de ${orgNombre}.`
            : `Se creó la cuenta de ${normalizedEmail}, pero no se pudo enviar el correo. Pídele que entre con "¿Olvidaste tu contraseña?" usando su correo.`);
        } catch (err: any) {
          // La invitación ya quedó: si no se pudo crear la cuenta, se le avisa para que se registre.
          console.error('No se pudo crear la cuenta del invitado:', err);
          await avisarInvitacionPendiente(orgNombre, normalizedEmail, rol);
          setSuccess(`Quedó la invitación y le enviamos un correo para que se registre con ${normalizedEmail}; al hacerlo quedará como ${ROLE_LABELS[rol]} de ${orgNombre}.`);
        }
        setPedirNombre(false);
        setNombreCompleto('');
      } else {
        const found = await findPlatformUserByEmail(normalizedEmail);
        if (found) {
          if (miembros.some((m) => m.id === found.uid)) {
            setError(`${found.email} ya es miembro de esta organización.`);
            return;
          }
          await addExistingUserToOrg(orgId, orgType, found.uid, found.email, rol);
          setSuccess(`${found.email} fue agregado como ${ROLE_LABELS[rol]} y le avisamos por correo.`);
        } else {
          setPedirNombre(true);
          return;
        }
      }
      setEmail('');
      setRol('miembro');
      await cargar();
    } catch (err: any) {
      setError(err.message || 'Error al invitar al miembro.');
    } finally {
      setInviting(false);
    }
  };

  const handleReenviar = async (inv: PendingInvitation & { id: string }) => {
    setError(null);
    setSuccess(null);
    setAccionEnCurso(inv.id);
    try {
      await reenviarInvitacion({ email: inv.email, orgId });
      setSuccess(`Le reenviamos la invitación a ${inv.email}, con un enlace nuevo para crear su contraseña.`);
    } catch (err: any) {
      setError(err.message || 'No se pudo reenviar la invitación.');
    } finally {
      setAccionEnCurso(null);
    }
  };

  const handleCambiarRol = async (uid: string, nuevoRol: OrgRoleAsignable) => {
    setError(null);
    setSuccess(null);
    setAccionEnCurso(uid);
    try {
      await changeOrgMemberRole(orgId, uid, nuevoRol);
      await cargar();
    } catch (err: any) {
      setError(err.message || 'Error al cambiar el rol.');
    } finally {
      setAccionEnCurso(null);
    }
  };

  const handleRemover = async (m: OrgMemberConNombre) => {
    if (!window.confirm(`¿Remover a ${m.displayName} de ${orgNombre}? Sus roles en proyectos puntuales no se ven afectados.`)) return;
    setError(null);
    setSuccess(null);
    setAccionEnCurso(m.id);
    try {
      await removeOrgMember(orgId, m.id);
      await cargar();
    } catch (err: any) {
      setError(err.message || 'Error al remover al miembro.');
    } finally {
      setAccionEnCurso(null);
    }
  };

  const handleRevocar = async (inv: PendingInvitation & { id: string }) => {
    if (!window.confirm(`¿Revocar la invitación pendiente de ${inv.email}?`)) return;
    setError(null);
    setSuccess(null);
    try {
      await revokeInvitation(orgId, inv.email);
      await cargar();
    } catch (err: any) {
      setError(err.message || 'Error al revocar la invitación.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/10 flex-shrink-0">
          <div>
            <h3 className="text-lg font-medium text-white">Miembros de {orgNombre}</h3>
            <p className="text-xs text-gray-500 mt-0.5">Gestiona quién pertenece a esta organización.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
          )}
          {success && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 p-3 rounded-lg text-xs">{success}</div>
          )}

          {loading ? (
            <div className="py-8 text-center text-gray-400 flex flex-col items-center gap-2">
              <RefreshCw className="w-5 h-5 animate-spin" />
              <p className="text-xs">Cargando miembros...</p>
            </div>
          ) : (
            <div className="space-y-2">
              {miembros.map((m) => {
                const esOwner = m.role === 'owner';
                const esYo = m.id === user?.uid;
                return (
                  <div
                    key={m.id}
                    className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl p-3"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white flex items-center gap-1.5 truncate">
                        {esOwner && <Crown className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />}
                        <span className="truncate">{m.displayName}</span>
                        {esYo && <span className="text-[10px] text-indigo-400 font-semibold flex-shrink-0">(Tú)</span>}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{m.email}</p>
                      <p className="text-[10px] text-gray-600 mt-0.5">Desde {fecha(m.joinedAt)}</p>
                    </div>

                    {puedoGestionar && !esOwner ? (
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <select
                          value={m.role === 'invitado' ? 'miembro' : m.role}
                          onChange={(e) => handleCambiarRol(m.id, e.target.value as OrgRoleAsignable)}
                          disabled={accionEnCurso === m.id}
                          className="bg-zinc-800 border border-white/10 rounded-lg px-2 py-1.5 text-xs disabled:opacity-50"
                          title="Cambiar rol"
                        >
                          <option value="miembro">Miembro</option>
                          <option value="admin">Admin</option>
                        </select>
                        <button
                          onClick={() => handleRemover(m)}
                          disabled={accionEnCurso === m.id}
                          className="p-1.5 text-gray-400 hover:text-red-400 disabled:opacity-50 transition-all"
                          title="Remover de la organización"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <span className="flex-shrink-0 text-[10px] font-semibold uppercase tracking-wide text-gray-500 bg-white/5 px-2 py-1 rounded-full flex items-center gap-1">
                        <Shield className="w-3 h-3" />
                        {ROLE_LABELS[m.role] || m.role}
                      </span>
                    )}
                  </div>
                );
              })}

              {pendientes.length > 0 && (
                <div className="pt-2">
                  <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                    <Clock className="w-3 h-3" /> Invitaciones pendientes
                  </p>
                  {pendientes.map((inv) => (
                    <div key={inv.id} className="flex items-center gap-3 bg-amber-500/5 border border-amber-500/15 rounded-xl p-3 mb-1.5">
                      <Mail className="w-4 h-4 text-amber-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-amber-200 truncate">{inv.email}</p>
                        <p className="text-[10px] text-gray-500">Se agregará como {ROLE_LABELS[inv.rol]} cuando entre por primera vez</p>
                      </div>
                      {puedoGestionar && (
                        <button
                          onClick={() => handleReenviar(inv)}
                          disabled={accionEnCurso === inv.id}
                          className="p-1.5 text-gray-400 hover:text-amber-300 transition-all flex-shrink-0 disabled:opacity-50"
                          title="Reenviar invitación (enlace nuevo para crear la contraseña)"
                        >
                          {accionEnCurso === inv.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </button>
                      )}
                      {puedoGestionar && (
                        <button
                          onClick={() => handleRevocar(inv)}
                          className="p-1.5 text-gray-400 hover:text-red-400 transition-all flex-shrink-0"
                          title="Revocar invitación"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {puedoGestionar && (
          <form onSubmit={handleInvitar} className="p-5 border-t border-white/10 flex-shrink-0 space-y-2">
            {/* Solo el OWNER de la org puede leer su propio límite (users/{uid}
                es self-or-admin en firestore.rules) — un Admin de org (no
                owner) no ve esta alerta, pero igual queda protegido por la
                regla real al invitar. */}
            {miRol === 'owner' && (
              <LimiteUsoAlerta
                cantidad={miembros.length}
                limite={profile?.limiteMiembrosPorOrganizacion}
                etiqueta="miembros"
              />
            )}
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider">
              <UserPlus className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> Invitar miembro
            </label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setPedirNombre(false); }}
                placeholder="email@empresa.com"
                className="flex-1 min-w-0 bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
                required
              />
              <select
                value={rol}
                onChange={(e) => setRol(e.target.value as OrgRoleAsignable)}
                className="flex-shrink-0 bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
              >
                <option value="miembro">Miembro</option>
                <option value="admin">Admin</option>
              </select>
              <button
                type="submit"
                disabled={inviting}
                className="flex-shrink-0 flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
              >
                {inviting ? <RefreshCw className="w-4 h-4 animate-spin" /> : pedirNombre ? 'Crear cuenta e invitar' : 'Invitar'}
              </button>
            </div>
            {pedirNombre && (
              <div className="space-y-1.5">
                <p className="text-xs text-amber-300">
                  {email.trim().toLowerCase()} todavía no tiene cuenta en OneHub. Escribe su nombre: le llegará un correo de bienvenida para crear su contraseña.
                </p>
                <input
                  type="text"
                  value={nombreCompleto}
                  onChange={(e) => setNombreCompleto(e.target.value)}
                  placeholder="Nombre completo"
                  className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
                  autoFocus
                  required
                />
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
